import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import './Ads.css';

const money = (value) => value == null ? '—' : new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD',
}).format(value);
const packMix = (packs) => `${packs.starter} / ${packs.basic} / ${packs.pro}${packs.unknown ? ` (+${packs.unknown} unknown)` : ''}`;

function ProjectionCells({ row }) {
  const result = { profitable: 'Projected profit', unprofitable: 'Projected loss', break_even: 'Break-even', unknown: 'Insufficient data' }[row.profitability];
  return <>
    <td>{row.buyers.toLocaleString()}</td>
    <td>{row.clicks?.toLocaleString() ?? '—'}</td>
    <td>{row.conversionRate == null ? '—' : `${row.conversionRate.toFixed(2)}%`}</td>
    <td className="ads-mix">{packMix(row.packs)}</td>
    <td>{money(row.ltgpPerBuyer)}</td>
    <td>{money(row.targetCpa)}</td>
    <td>{money(row.totalLtgp)}</td>
    <td>{money(row.estimatedSpend)}</td>
    <td>{money(row.estimatedCpa)}</td>
    <td className={`ads-result ads-result-${row.profitability}`}>
      <strong>{money(row.projectedProfit)}</strong>
      <small>{result}</small>
      {row.trackingIssue && <small>{row.trackingIssue}</small>}
    </td>
  </>;
}

export default function Ads() {
  const [days, setDays] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setData(null);
    setError('');
    axios.get(`${API_BASE_URL}/admin/ads`, { params: { days } })
      .then(({ data: result }) => { if (active) setData(result); })
      .catch((err) => { if (active) setError(err.response?.data?.error || 'Could not load ad projections.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [days, refresh]);

  return <main className="ads-page">
    <h1>Ad offer performance</h1>
    <div className="ads-controls" aria-label="Date range">
      <button type="button" aria-pressed={days === 1} onClick={() => setDays(1)}>Today</button>
      <button type="button" aria-pressed={days === 7} onClick={() => setDays(7)}>7 Days</button>
      <button type="button" disabled={loading} onClick={() => setRefresh((value) => value + 1)}>Refresh</button>
    </div>
    <p>{days === 1 ? 'Today' : 'Today and the previous 6 days'} · Chicago time · New buyers from tracked ads</p>
    {loading && <p role="status">Loading ad projections…</p>}
    {error && <p role="alert" className="ads-error">{error}</p>}
    {data && <>
      <p className="ads-period">{data.startDate}{data.days === 7 ? ` – ${data.endDate}` : ''} · Updated {new Date(data.asOf).toLocaleTimeString('en-US', { timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit' })}</p>
      <div className="ads-table-wrap">
        <table>
          <caption>90-day profit projection · Assumed CPC {money(data.assumptions.assumedCpc)}</caption>
          <thead><tr>
            <th scope="col">Ad source</th>
            <th scope="col">Buyers</th>
            <th scope="col">Tracked clicks<br /><small>Visit proxy</small></th>
            <th scope="col">Buyer CVR</th>
            <th scope="col">Pack mix<br /><small>200 / 1,200 / 4,000</small></th>
            <th scope="col">LTGP / buyer<br /><small>Break-even CPA</small></th>
            <th scope="col">Working CPA target</th>
            <th scope="col">Total projected LTGP</th>
            <th scope="col">Estimated spend<br /><small>@ {money(data.assumptions.assumedCpc)} CPC</small></th>
            <th scope="col">Estimated CPA</th>
            <th scope="col">Projected profit / loss<br /><small>After estimated spend</small></th>
          </tr></thead>
          <tbody>
            {data.rows.map((row) => <tr key={row.ad}>
              <th scope="row">{row.ad.replace(/^(rise|safev)-/, '')}{row.ad.includes('{{') && <small className="ads-tag-note">Unresolved ad name</small>}</th>
              <ProjectionCells row={row} />
            </tr>)}
            {!data.rows.length && <tr><td colSpan={11} className="ads-empty">No tracked ad activity in this period.</td></tr>}
          </tbody>
          <tfoot><tr><th scope="row">Blended total</th><ProjectionCells row={data.totals} /></tr></tfoot>
        </table>
      </div>
      <p className="ads-note">Blended LTGP / buyer = total projected LTGP ÷ total buyers. The CPA target is {Math.round(data.assumptions.targetCpaShare * 100)}% of LTGP. Both are weighted by buyer count.</p>
      <p className="ads-note">Estimated spend = tracked clicks × {money(data.assumptions.assumedCpc)}. Projected profit = LTGP − estimated spend. Clicks use distinct recorded ad click IDs on landing, pricing and visualizer pages, not Meta billing data. Missed visits can understate spend.</p>
      <p className="ads-note">Blended totals exclude ads with insufficient data{data.totals.incompleteAds ? ` (${data.totals.incompleteAds} excluded)` : ''}. Blended spend includes eligible ads with no buyers; blended CPA = included estimated spend ÷ included buyers. Buyers are grouped by purchase date, so delayed purchases may come from earlier clicks.</p>
      <p className="ads-note">Today is still in progress. Projections include collected first-day purchases and top-ups, plus expected repeat profit for each purchased pack. Small buyer counts are preliminary.</p>
      {data.rows.some((row) => row.unmodeledBuyers > 0) && <p role="status" className="ads-error">Some buyers lack a matched package or historical model. Affected ads have no projection and are excluded from blended totals.</p>}
      <details>
        <summary>Model assumptions</summary>
        <p>Based on {data.modelBuyers.toLocaleString()} historical buyers with at least {data.assumptions.matureDays} days of history. Assumes {Math.round(data.assumptions.costRate * 100)}% blended costs, {Math.round(data.assumptions.retention * 100)}% retention between {data.assumptions.refillCycles} refill cycles, and {data.assumptions.repeatMultiple}× first-week repeat spending over {data.assumptions.horizonDays} days.</p>
        <p>First-refill revenue is measured through day 10 and capped at one $19.99 bill per buyer. Wallet cohorts whose first bill was due before {data.assumptions.walletBillingFrom} are excluded from the historical model.</p>
        <p>LTGP is before advertising; projected profit deducts estimated click spend. Neither is realized profit. Sources use saved acquisition tags. {data.excludedBuyers} buyers without a tracked ad source are excluded from this table and its total. Ads with tracked visits but no buyers are included.</p>
      </details>
    </>}
  </main>;
}
